import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { newId, tryParseBirr } from '@dairy/shared';
import { useIsOwner } from '@/auth/useSession';
import { DateField } from '@/components/DateField';
import { MoneyInput } from '@/components/inputs';
import { toast } from '@/components/toast';
import { Button, Card, Field, PageHeader, Segmented, Select, Textarea } from '@/components/ui';
import { errorMessage } from '@/components/errors';
import { db } from '@/db/db';
import { saveMany, type Change } from '@/db/repo';
import { useToday } from '@/db/hooks';
import { reminderChangesFor } from '@/features/events/effects';

type ExitType = 'DEAD' | 'SOLD' | 'CULLED';

/** Screen 9 — Exit animal (AR-4): SOLD and CULLED owner only, DEAD both; SOLD continues to the animal sale (IN-5). */
export function ExitAnimalPage() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isOwner = useIsOwner();
  const today = useToday();
  const animal = useLiveQuery(() => db.animals.get(id), [id]);
  const buyers = useLiveQuery(() => db.buyers.toArray(), []) ?? [];
  const [type, setType] = useState<ExitType>('DEAD');
  const [date, setDate] = useState(today);
  const [note, setNote] = useState('');
  const [amount, setAmount] = useState('');
  const [buyerId, setBuyerId] = useState('');
  const [error, setError] = useState<string | null>(null);
  if (!animal) return null;
  const types: ExitType[] = isOwner ? ['DEAD', 'SOLD', 'CULLED'] : ['DEAD'];

  const submit = async () => {
    if (date < animal.birthDate || date > today) return setError(t('animals.form.invalidDate'));
    if (!note.trim()) return setError(t('animals.exit.noteRequired'));
    const amt = tryParseBirr(amount);
    if (type === 'SOLD' && (!amt.ok || !amt.santim)) return setError(t('money.amountRequired'));
    const updated = { ...animal, status: type, exitDate: date, exitNote: note.trim() };
    const events = (await db.events.where('animalId').equals(id).toArray()) as never;
    const changes: Change[] = [{ entity: 'animal', op: 'upsert', record: updated }, ...(await reminderChangesFor(updated, events))];
    if (type === 'SOLD' && amt.ok && amt.santim) {
      changes.push({
        entity: 'sale',
        op: 'upsert',
        record: { id: newId(), type: 'ANIMAL', date, animalId: id, buyerId: buyerId || null, amountSantim: amt.santim, description: note.trim() },
      });
    }
    try {
      await saveMany(changes);
      toast(t('common.saved'));
      navigate(`/animals/${id}`, { replace: true });
    } catch (e) {
      setError(errorMessage(t, e));
    }
  };

  return (
    <div className="mx-auto max-w-lg">
      <PageHeader title={t('animals.exit.title', { tag: animal.tag })} />
      <Card className="space-y-4">
        <Field label={t('animals.exit.typeLabel')}>
          <Segmented value={type} onChange={setType} options={types.map((x) => ({ value: x, label: t(`animals.exit.type.${x}`) }))} />
        </Field>
        <Field label={t('common.date')}>
          <DateField value={date} max={today} min={animal.birthDate} onChange={setDate} />
        </Field>
        <Field label={type === 'DEAD' ? t('animals.exit.cause') : t('animals.exit.reason')}>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
        </Field>
        {type === 'SOLD' ? (
          <>
            <Field label={t('money.amount')}>
              <MoneyInput value={amount} onChange={(e) => setAmount(e.target.value)} />
            </Field>
            <Field label={t('money.buyerOptional')}>
              <Select value={buyerId} onChange={(e) => setBuyerId(e.target.value)}>
                <option value="">{t('money.noBuyer')}</option>
                {buyers.filter((b) => b.active).map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </Select>
            </Field>
          </>
        ) : null}
        {error ? <p className="text-red-700">{error}</p> : null}
        <Button size="lg" variant="danger" className="w-full" onClick={() => void submit()}>
          {t('animals.exit.confirm')}
        </Button>
      </Card>
    </div>
  );
}
